import { useEffect } from 'react';

/**
 * Adds `.in-view` to any element with `.reveal`, `.reveal-zoom`,
 * `.reveal-left`, or `.reveal-right` as it scrolls into view.
 *
 * Robust against:
 *  - Elements present at mount
 *  - Newly added nodes (e.g. tab-switched content)
 *  - Reused DOM nodes where the reveal class is re-applied via attribute change
 */
const SELECTOR = '.reveal, .reveal-zoom, .reveal-left, .reveal-right';

const hasRevealClass = (el) =>
  el.classList.contains('reveal') ||
  el.classList.contains('reveal-zoom') ||
  el.classList.contains('reveal-left') ||
  el.classList.contains('reveal-right');

const useScrollReveal = () => {
  useEffect(() => {
    const observed = new WeakSet();

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            intersectionObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    const tryObserve = (el) => {
      if (!(el instanceof Element)) return;
      if (!hasRevealClass(el)) return;
      if (el.classList.contains('in-view')) return;
      if (observed.has(el)) return;
      observed.add(el);
      intersectionObserver.observe(el);
    };

    const scan = (root) => {
      if (!(root instanceof Element)) return;
      tryObserve(root);
      root.querySelectorAll?.(SELECTOR).forEach(tryObserve);
    };

    // Initial pass
    scan(document.body);

    // Watch DOM for new nodes AND attribute changes (covers React DOM-reuse case)
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        if (m.type === 'childList') {
          m.addedNodes.forEach(scan);
        } else if (m.type === 'attributes' && m.attributeName === 'class') {
          const el = m.target;
          if (el instanceof Element && hasRevealClass(el) && !el.classList.contains('in-view')) {
            // Re-observe — WeakSet membership doesn't matter here because we
            // explicitly want to re-check this node.
            observed.delete?.(el);
            intersectionObserver.observe(el);
          }
        }
      });
    });
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);
};

export default useScrollReveal;
